import { forwardRef, useMemo, useEffect, useRef } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormSelect, { Props as FormSelectProps } from './FormSelect';

type Props = Omit<FormSelectProps, 'options'>;

const ADD_PROPERTY_LOCATION_VALUE = '__add_property_location__';

const FormPropertyLocationSelect = forwardRef<HTMLDivElement, Props>(
  ({ placeholder = 'Select Property Location', ...props }, ref) => {
    const { propertyLocations, isFetching } = usePropertyManager();
    const history = useHistory();
    const formContext = useFormContext();
    const { setValue, getValues } = formContext || {};
    const previousLocationsCountRef = useRef<number>(0);
    const isNavigatingToCreateRef = useRef<boolean>(false);

    const options = useMemo(
      () => [
        ...propertyLocations.map(location => ({
          label: location.name,
          value: location.id,
        })),
        {
          label: '+ Add Property Location',
          value: ADD_PROPERTY_LOCATION_VALUE,
        },
      ],
      [propertyLocations]
    );

    // Watch for form value changes to handle navigation
    const watchedValue = useWatch({
      control: props.control,
      name: props.name,
    });

    // Auto-select first location if no location is selected and locations are available
    useEffect(() => {
      if (!setValue || !getValues) return; // Skip if form context is not available

      const currentValue = getValues(props.name);
      if (!currentValue && propertyLocations.length > 0 && !isFetching) {
        setValue(props.name, propertyLocations[0].id);
      }
    }, [propertyLocations, isFetching, setValue, getValues, props.name]);

    // Auto-select newly added location
    useEffect(() => {
      if (!setValue || !getValues || isFetching) return;

      const currentLocationsCount = propertyLocations.length;
      const previousCount = previousLocationsCountRef.current;

      // If we just returned from creating a location and there's a new location
      if (
        isNavigatingToCreateRef.current &&
        currentLocationsCount > previousCount &&
        propertyLocations.length > 0
      ) {
        // Select the most recently added location (assuming it's the last one)
        const newLocation = propertyLocations[propertyLocations.length - 1];
        setValue(props.name, newLocation.id);
        isNavigatingToCreateRef.current = false;
      }

      previousLocationsCountRef.current = currentLocationsCount;
    }, [propertyLocations, isFetching, setValue, getValues, props.name]);

    useEffect(() => {
      if (watchedValue === ADD_PROPERTY_LOCATION_VALUE) {
        isNavigatingToCreateRef.current = true;
        history.push(PMPRoutes.propertyLocationCreate);
      }
    }, [watchedValue, history]);

    return (
      <FormSelect
        {...props}
        placeholder={placeholder}
        disabled={isFetching || props.disabled}
        options={options}
        ref={ref}
      />
    );
  }
);

export default FormPropertyLocationSelect;
